// Thin fetch wrapper honouring the { ok, data } / { ok, error } envelope.
// Every request carries the session cookie; API is same-origin in prod
// and proxied in dev — no CORS to worry about.
//
// When running inside Telegram Mini App, we also attach the signed
// `initData` blob as `X-Init-Data`; the backend HMAC-verifies it against
// the bot token and treats the resulting user as a first-class identity
// (see docs/SPEC.md §9). Outside Telegram the header is simply absent.

import { tgInitData } from "./tg.js";

export interface ApiOk<T> {
  ok: true;
  data: T;
}
export interface ApiErr {
  ok: false;
  error: { code: string; message: string; meta?: Record<string, unknown> };
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly meta?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  return call<T>("GET", path);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return call<T>("POST", path, body);
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const initData = tgInitData();
    if (initData) headers["X-Init-Data"] = initData;

    const init: RequestInit = { method, credentials: "same-origin" };
    if (Object.keys(headers).length > 0) init.headers = headers;
    if (body !== undefined) init.body = JSON.stringify(body);
    res = await fetch(path, init);
  } catch {
    throw new ApiError("NETWORK", "network error", 0);
  }
  let json: ApiOk<T> | ApiErr;
  try {
    json = (await res.json()) as ApiOk<T> | ApiErr;
  } catch {
    throw new ApiError("INTERNAL_ERROR", "bad json", res.status);
  }
  if (!json.ok) {
    throw new ApiError(
      json.error.code,
      json.error.message,
      res.status,
      json.error.meta,
    );
  }
  return json.data;
}
